/**
 * Where the chairs are — computed, never stored.
 *
 * The database holds a table's geometry (shape + seats per side) and nothing
 * about where a chair sits, so a seat position is a pure function of that
 * geometry. This module is that function, and it is a deliberate duplicate of
 * the server's: the API returns `seats[]` with the same numbers, but the render
 * must not depend on an optional payload field. `?include=geometry=false` is a
 * supported request, and a client that only knows how to draw the server's
 * answer draws an empty room the first time someone sends it.
 *
 * The ordinal is the contract, not the coordinate. Assignments key off
 * (table_id, seat_index), which is why moving, rotating or relabelling a table
 * costs nothing — and why GROWING a side renumbers every later chair. Nothing
 * here caches: recompute from the table row on every render, or a resize leaves
 * the room drawn one chair out of step with who is actually sitting in it.
 */
import type { SeatingTable, SeatingTableShape } from '../types/seating';

/** Centimetres of table edge per chair. The chart's own value overrides it. */
export const DEFAULT_SEAT_PITCH_CM = 60;

/** Round: 12 o'clock, clockwise. Rectangle: the perimeter walk, from the top-left. */
export type SeatSide = 'round' | 'top' | 'right' | 'bottom' | 'left';

export interface SeatPosition {
  seatIndex: number;
  side: SeatSide;
  /** Table-local centimetres, origin at the table centre, y pointing down. */
  x: number;
  y: number;
  /** Which way the chair faces, degrees, y-down screen convention. */
  facingDeg: number;
}

export type TableBody =
  | { kind: 'circle'; radius: number }
  | { kind: 'rect'; width: number; height: number };

export interface TableGeometry {
  shape: SeatingTableShape;
  capacity: number;
  body: TableBody;
  seats: SeatPosition[];
  /** Chair radius in the same centimetres, so one viewBox fits both. */
  seatRadius: number;
  /** Bounding box of table + chairs, centred on the origin. */
  extent: { width: number; height: number };
}

/** Counts arrive from JSON, so treat anything that is not a positive integer as zero. */
function count(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function shapeOf(table: Pick<SeatingTable, 'shape'>): SeatingTableShape {
  return table.shape === 'rectangle' || table.shape === 'square' ? table.shape : 'round';
}

/** What the database's generated `capacity` column would say for these counts. */
export function tableCapacity(table: Partial<SeatingTable>): number {
  if (shapeOf({ shape: table.shape ?? 'round' }) === 'round') return count(table.seatCount);
  return (
    count(table.seatsTop) + count(table.seatsBottom) + count(table.seatsLeft) + count(table.seatsRight)
  );
}

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function roundSeats(seatCount: number, radius: number, gap: number): SeatPosition[] {
  const seats: SeatPosition[] = [];
  for (let k = 0; k < seatCount; k += 1) {
    // Seat 0 at 12 o'clock, clockwise. -90deg is straight up because y grows
    // downwards on screen, which is also the convention the API documents.
    const deg = -90 + (k * 360) / seatCount;
    const rad = (deg * Math.PI) / 180;
    seats.push({
      seatIndex: k,
      side: 'round',
      x: (radius + gap) * Math.cos(rad),
      y: (radius + gap) * Math.sin(rad),
      facingDeg: normalizeDeg(deg + 180),
    });
  }
  return seats;
}

/**
 * The perimeter walk: top left-to-right, then the right head downwards, then the
 * bottom right-to-left, then the left head upwards. Pinned in one direction on
 * purpose — a client that walked it the other way would seat the whole room
 * mirror-image while agreeing with the server on every ordinal.
 */
function rectSeats(
  counts: { top: number; right: number; bottom: number; left: number },
  size: { width: number; height: number },
  gap: number,
): SeatPosition[] {
  const halfW = size.width / 2;
  const halfH = size.height / 2;
  const seats: SeatPosition[] = [];
  let index = 0;

  for (let j = 0; j < counts.top; j += 1) {
    seats.push({
      seatIndex: index++,
      side: 'top',
      x: -halfW + (size.width * (j + 0.5)) / counts.top,
      y: -halfH - gap,
      facingDeg: 90,
    });
  }
  for (let j = 0; j < counts.right; j += 1) {
    seats.push({
      seatIndex: index++,
      side: 'right',
      x: halfW + gap,
      y: -halfH + (size.height * (j + 0.5)) / counts.right,
      facingDeg: 180,
    });
  }
  for (let j = 0; j < counts.bottom; j += 1) {
    seats.push({
      seatIndex: index++,
      side: 'bottom',
      x: halfW - (size.width * (j + 0.5)) / counts.bottom,
      y: halfH + gap,
      facingDeg: 270,
    });
  }
  for (let j = 0; j < counts.left; j += 1) {
    seats.push({
      seatIndex: index++,
      side: 'left',
      x: -halfW - gap,
      y: halfH - (size.height * (j + 0.5)) / counts.left,
      facingDeg: 0,
    });
  }
  return seats;
}

function extentOf(body: TableBody, seats: SeatPosition[], seatRadius: number) {
  let halfW = body.kind === 'circle' ? body.radius : body.width / 2;
  let halfH = body.kind === 'circle' ? body.radius : body.height / 2;
  for (const seat of seats) {
    halfW = Math.max(halfW, Math.abs(seat.x) + seatRadius);
    halfH = Math.max(halfH, Math.abs(seat.y) + seatRadius);
  }
  return { width: halfW * 2, height: halfH * 2 };
}

/**
 * The whole render, in table-local centimetres.
 *
 * Margin (half a pitch) is the table overhanging its end chairs; gap (0.35 of a
 * pitch) is how far a chair sits off the edge. Both are pure scale — change the
 * chart's pitch and the room redraws with every ordinal untouched.
 */
export function computeTableGeometry(
  table: Partial<SeatingTable>,
  pitchCm: number = DEFAULT_SEAT_PITCH_CM,
): TableGeometry {
  const pitch = Number.isFinite(pitchCm) && Number(pitchCm) > 0 ? Number(pitchCm) : DEFAULT_SEAT_PITCH_CM;
  const gap = 0.35 * pitch;
  const margin = 0.5 * pitch;
  const seatRadius = 0.28 * pitch;
  const shape = shapeOf({ shape: table.shape ?? 'round' });

  if (shape === 'round') {
    const seatCount = count(table.seatCount);
    // Floor the radius so a two-top is still a table rather than a dot with two
    // chairs overlapping in the middle of it.
    const radius = Math.max((seatCount * pitch) / (2 * Math.PI), 0.8 * pitch);
    const seats = roundSeats(seatCount, radius, gap);
    const body: TableBody = { kind: 'circle', radius };
    return {
      shape,
      capacity: seatCount,
      body,
      seats,
      seatRadius,
      extent: extentOf(body, seats, seatRadius),
    };
  }

  const counts = {
    top: count(table.seatsTop),
    right: count(table.seatsRight),
    bottom: count(table.seatsBottom),
    left: count(table.seatsLeft),
  };
  let width = Math.max(counts.top, counts.bottom) * pitch + 2 * margin;
  let height = Math.max(counts.left, counts.right) * pitch + 2 * margin;
  if (shape === 'square') {
    // A square renders 1:1 by definition; the API refuses asymmetric opposite
    // sides, so the longer axis is the one that has to fit.
    width = Math.max(width, height);
    height = width;
  }

  const seats = rectSeats(counts, { width, height }, gap);
  const body: TableBody = { kind: 'rect', width, height };
  return {
    shape,
    capacity: counts.top + counts.right + counts.bottom + counts.left,
    body,
    seats,
    seatRadius,
    extent: extentOf(body, seats, seatRadius),
  };
}

/**
 * What a host calls this chair out loud. Seat numbers are 1-based — nobody says
 * "seat zero" — and a lone chair on a short side of a long table is the head,
 * which is the seat a host actually has a name for.
 */
export function seatLabel(geometry: TableGeometry, seatIndex: number): string {
  const seat = geometry.seats[seatIndex];
  const number = `Seat ${seatIndex + 1}`;
  if (!seat || geometry.shape === 'round') return number;

  const isSingleEnd =
    (seat.side === 'right' || seat.side === 'left') &&
    geometry.seats.filter((other) => other.side === seat.side).length === 1;
  return isSingleEnd ? `${number} · head of the table` : number;
}
